import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
// @material-ui/core components
import Box from "@material-ui/core/Box";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Container from "@material-ui/core/Container";
import CardTableContainer from "components/Cards/Tables/CardTableContainer";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/core/styles";

import componentStylesGeneric from "assets/theme/views/admin/generic";
import GenericBackdrop from "components/Backdrops/GenericBackdrop";
import GenericButton from "components/Buttons/GenericButton";
import LocationHeader from "components/Headers/LocationHeader";
import PageTitleHeader from "components/Headers/PageTitleHeader";
import { getBenefitDetails, exportBenefitDetailsReport } from "crud/benefits.crud";
import moment from "moment";
import { BENEFIT } from "utils/const";
import { formatCurrency } from "utils/string_utils";

const useStylesGeneric = makeStyles(componentStylesGeneric);

const BENEFIT_TYPE_LABEL = {
  [BENEFIT.MICROCREDIT]: "Microcrédito",
  [BENEFIT.PROFESSIONAL_KIT]: "Kit Profissional",
  [BENEFIT.PROFESSIONAL_WALLET]: "Carteira Profissional",
  [BENEFIT.CAPACITA]: "Capacita",
  [BENEFIT.AVANCO]: "Avanço",
  [BENEFIT.PROFESSIONAL_INTERNSHIP]: "Estágio Profissional",
  [BENEFIT.PROFESSIONAL_KIT_MICROCREDIT]: "Kit Profissional / Microcrédito",
};

function MyBenefitsDetails() {
  const classes = useStylesGeneric();
  const history = useHistory();
  const location = useLocation();
  const { applicationNumber } = useParams();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const loanId = location.state?.loanId;
  const subsidyId = location.state?.subsidyId;

  const benefitTypeLabel = data?.benefitType
    ? (BENEFIT_TYPE_LABEL[data.benefitType] ?? data.benefitType)
    : (BENEFIT_TYPE_LABEL[location.state?.benefitType] ?? location.state?.benefitType ?? "");

  const isSubsidy = data ? (data.subsidyId != null && data.subsidyId !== 0) : Boolean(subsidyId);

  useEffect(() => {
    setLoading(true);
    getBenefitDetails({
      LoanId: loanId || undefined,
      SubsidyId: subsidyId || undefined,
    })
      .then((res) => {
        setData(res.data);
      })
      .catch(() => {
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId, subsidyId]);

  const handleExport = () => {
    setLoading(true);
    exportBenefitDetailsReport({
      LoanId: loanId || undefined,
      SubsidyId: subsidyId || undefined,
    })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank");
        URL.revokeObjectURL(url);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <GenericBackdrop open={loading} />

      <Container maxWidth={false} component={Box} classes={{ root: classes.containerRoot }}>
        <LocationHeader
          section={"Detalhes do Benefício Recebido"}
          subsection={"Benefícios Recebidos"}
          subsectionLink={"/admin/my-benefits"}
        />

        <Box display="flex" alignItems="flex-start" justifyContent="flex-start" flexWrap="wrap" style={{ gap: "12px" }}>
          <PageTitleHeader title={[benefitTypeLabel, applicationNumber].filter(Boolean).join(" - ") || "Detalhes do Benefício Recebido"} />
          <GenericButton
            typeSubmit={false}
            color="tertiary"
            onClick={handleExport}
          >
            Exportar Detalhes
          </GenericButton>
        </Box>

        {/* Benefit Details Card */}
        <Card classes={{ root: classes.cardRoot }}>
          <CardHeader
            classes={{
              action: classes.cardHeaderAction,
              title: classes.cardHeader,
            }}
            title={benefitTypeLabel || "Detalhes do Benefício"}
          />
          <CardContent>
            <Grid container>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Nome do Beneficiário</FormLabel>
                  <Box>{data?.beneficiaryName ?? "-"}</Box>
                </FormGroup>
              </Grid>
              {isSubsidy && (
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormLabel>Conta Bancária do Beneficiário</FormLabel>
                    <Box>{data?.beneficiaryBankAccount ?? "-"}</Box>
                  </FormGroup>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>NIF</FormLabel>
                  <Box>{data?.nif ?? "-"}</Box>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Documento de Identificação</FormLabel>
                  <Box>{data?.identificationDocument ?? "-"}</Box>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>{isSubsidy ? "Província do Beneficiário" : "Província"}</FormLabel>
                  <Box>{data?.province ?? "-"}</Box>
                </FormGroup>
              </Grid>
              {isSubsidy && data?.cooperativeName && (
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormLabel>Nome da Cooperativa</FormLabel>
                    <Box>{data.cooperativeName}</Box>
                  </FormGroup>
                </Grid>
              )}
              {isSubsidy && data?.cooperativeNIF && (
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormLabel>NIF da Cooperativa</FormLabel>
                    <Box>{data.cooperativeNIF}</Box>
                  </FormGroup>
                </Grid>
              )}
              {isSubsidy && data?.cooperativeNISS && (
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormLabel>NISS da Cooperativa</FormLabel>
                    <Box>{data.cooperativeNISS}</Box>
                  </FormGroup>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Email</FormLabel>
                  <Box>{data?.email ?? "-"}</Box>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Telefone</FormLabel>
                  <Box>{data?.phone ?? "-"}</Box>
                </FormGroup>
              </Grid>
              {isSubsidy && data?.cooperativeProvince && (
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormLabel>Província da Cooperativa</FormLabel>
                    <Box>{data.cooperativeProvince}</Box>
                  </FormGroup>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Número Candidatura</FormLabel>
                  <Box>{data?.applicationNumber ?? "-"}</Box>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Programa</FormLabel>
                  <Box>{data?.program ?? "-"}</Box>
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Subprograma</FormLabel>
                  <Box>{data?.subProgram ?? "-"}</Box>
                </FormGroup>
              </Grid>
              {isSubsidy ? (
                <>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Iniciativa</FormLabel>
                      <Box>{data?.initiative ?? "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Valor Mensal Aprovado</FormLabel>
                      <Box>{data?.approvedMonthlyAmount != null ? formatCurrency(data.approvedMonthlyAmount) : "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Número de Meses</FormLabel>
                      <Box>{data?.numberOfMonths ?? "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>% Retenção</FormLabel>
                      <Box>{data?.retentionPercentage != null ? `${data.retentionPercentage}%` : "-"}</Box>
                    </FormGroup>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Valor da Dívida</FormLabel>
                      <Box>{data?.debtAmount != null ? formatCurrency(data.debtAmount) : "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Prazo de Cumprimento</FormLabel>
                      <Box>{data?.debtDeadline ? moment(data.debtDeadline).format("DD/MM/YYYY") : "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Valor da Dívida Actualizado</FormLabel>
                      <Box>{data?.updatedDebtAmount != null ? formatCurrency(data.updatedDebtAmount) : "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Valor Pago</FormLabel>
                      <Box>{data?.paidAmount != null ? formatCurrency(data.paidAmount) : "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Valor de Juro</FormLabel>
                      <Box>{data?.interestAmount != null ? formatCurrency(data.interestAmount) : "-"}</Box>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormLabel>Em Incumprimento</FormLabel>
                      <Box>{data?.isMissingPayments != null ? (data.isMissingPayments ? "Sim" : "Não") : "-"}</Box>
                    </FormGroup>
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormLabel>Estado do Benefício</FormLabel>
                  <Box>{data?.benefitState ?? "-"}</Box>
                </FormGroup>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Payment History / Payments Card */}
        {isSubsidy ? (
          <CardTableContainer
            title="Pagamentos"
            count={data?.payments?.length ?? 0}
            style={{ marginTop: "24px" }}
          >
            <TableContainer>
              <Box component={Table} alignItems="center" marginBottom="0!important">
                <TableHead>
                  <TableRow>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Período</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Valor Mensal Aprovado</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Valor Mensal Recebido</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Comprovativo de Pagamento</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Observações</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.payments ?? []).map((payment, idx) => (
                    <TableRow key={idx}>
                      <TableCell classes={{ root: classes.tableCellRoot }}>{payment.period ?? "-"}</TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        {payment.approvedMonthlyAmount != null ? formatCurrency(payment.approvedMonthlyAmount) : "-"}
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        {payment.receivedMonthlyAmount != null ? formatCurrency(payment.receivedMonthlyAmount) : "-"}
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>{payment.paymentProof ?? "-"}</TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>{payment.observations ?? "-"}</TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>{payment.state ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                  {(!data?.payments || data.payments.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" classes={{ root: classes.tableCellRoot }}>
                        Sem pagamentos
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Box>
            </TableContainer>
          </CardTableContainer>
        ) : (
          <CardTableContainer
            title="Histórico de Pagamentos"
            count={data?.paymentHistory?.length ?? 0}
            style={{ marginTop: "24px" }}
          >
            <TableContainer>
              <Box component={Table} alignItems="center" marginBottom="0!important">
                <TableHead>
                  <TableRow>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Data de Pagamento</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Valor Pago</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Valor Juro</TableCell>
                    <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellRootHead }}>Em Incumprimento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.paymentHistory ?? []).map((payment, idx) => (
                    <TableRow key={idx}>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        {payment.paymentDate ? moment(payment.paymentDate).format("DD/MM/YYYY") : "-"}
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        {payment.paidAmount != null ? formatCurrency(payment.paidAmount) : "-"}
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        {payment.interestAmount != null ? formatCurrency(payment.interestAmount) : "-"}
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCellRoot }}>
                        {payment.isMissingPayments != null ? (payment.isMissingPayments ? "Sim" : "Não") : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data?.paymentHistory || data.paymentHistory.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" classes={{ root: classes.tableCellRoot }}>
                        Sem histórico de pagamentos
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Box>
            </TableContainer>
          </CardTableContainer>
        )}

        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" mt={2} mb={2} style={{ gap: "12px" }}>
          <GenericButton
            typeSubmit={false}
            color="secondary"
            onClick={() => history.push("/admin/my-benefits")}
          >
            Voltar à Página Inicial
          </GenericButton>
        </Box>
      </Container>
    </>
  );
}

export default MyBenefitsDetails;
